import {
    Button,
    Box,
    Typography,
    Divider,
    TextField,
    IconButton,
    LinearProgress,
    Switch,
    Tooltip,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import RuleSet from './RuleSet'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { useGetRuleSets } from './hooks/getRuleSets'
import { useCreateRuleSet } from './hooks/createRuleSet'
import { useUpdateRuleSetName } from './hooks/updateRuleSet'
import DeleteIcon from '@mui/icons-material/Delete'
import ConfirmationDialog from '../../Common/ConfirmationDialog'
import { useDeleteRuleSet } from './hooks/deleteRuleSet'
import { EditingRuleSet, IRuleSet } from '../interfaces'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { useTranslation } from 'react-i18next'
import { UserPermissions } from '../../../constants/constants'
import { usePermissions } from '../../../hooks/usePermissions'

const defultEditRuleSetValues: EditingRuleSet = {
    id: '',
    name: '',
    isDefault: false,
}
interface BusinessRulesPropsType {
    maxHeight: string //this for fixed table height. for maintaing scroll with in the view
}
const BusinessRules = (props: BusinessRulesPropsType): ReactElement => {
    const { maxHeight } = props
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [newRuleSet, setNewRuleSet] = useState<boolean>(false)
    const [newRuleSetName, setNewRuleSetName] = useState<string>('')

    const [editingRuleSet, setEditingRuleSet] = useState<EditingRuleSet | null>(
        null,
    )

    const org = useAppSelector((state) => state.org.org)
    const entityId = useAppSelector(
        (state) => state.dtStudio.selectedEntity?.id,
    )
    const orgId = org?.id ? org?.id : ''

    const { hasPermission } = usePermissions()

    const hasWriteAccess = useMemo(
        () => hasPermission(UserPermissions.ENTITY_WRITER),
        [hasPermission],
    )

    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const [ruleSetIdForDeletion, setRuleSetIdForDeletion] = useState<
        string | null
    >(null)

    const [reload, setReload] = useState<boolean>(false)

    const {
        data: fetchedRuleSets,
        // isError: isLoadingRuleSetsError,
        isFetching: isFetchingRuleSets,
        isLoading: isLoadingRuleSets,
    } = useGetRuleSets(orgId, entityId, reload)

    const {
        mutateAsync: createRuleSet,
        // isPending: isCreatingRuleSet
    } = useCreateRuleSet(orgId, entityId)
    const {
        mutateAsync: updateRuleSetName,
        // isPending: isUpdatingRelation
    } = useUpdateRuleSetName(orgId, entityId)
    const {
        mutateAsync: deleteRuleSet,
        // isPending: isDeletingRelation
    } = useDeleteRuleSet(orgId, entityId)

    useEffect(() => {
        dispatch(updateLoader(isFetchingRuleSets))
        return () => {
            dispatch(updateLoader(false))
        }
    }, [isFetchingRuleSets, dispatch])

    const handleNewRuleSetCreation = () => {
        setNewRuleSet(false)
        createRuleSet(newRuleSetName)
        setNewRuleSetName('')
    }
    const cancelNewRuleSetCreation = () => {
        setNewRuleSet(false)
        setNewRuleSetName('')
    }

    const handleEditClick = (ruleSet: IRuleSet) => {
        setEditingRuleSet({
            id: ruleSet?.id,
            name: ruleSet?.name,
            isDefault: ruleSet?.isDefault,
        })
    }

    const cancelEditRuleSetName = () => {
        setEditingRuleSet(null)
    }
    const saveEditRuleSetName = () => {
        // Call API to update rule set name
        const ruleset = fetchedRuleSets?.data?.find(
            (ruleSet) => ruleSet.id === editingRuleSet?.id,
        )
        updateRuleSetName({
            id: editingRuleSet?.id || '',
            name: editingRuleSet?.name || '',
            isDefault: editingRuleSet?.isDefault || false,
            readOnly: ruleset?.readOnly || false,
            rules: ruleset?.rules ?? [],
        })
        cancelEditRuleSetName()
    }

    // DELETE action
    const openDeleteConfirmModal = (id: string) => {
        setRuleSetIdForDeletion(id)
        setOpenDeleteDialog(true)
    }

    const handleDeleteDialogOperation = async () => {
        if (!ruleSetIdForDeletion) {
            return
        }
        await deleteRuleSet(ruleSetIdForDeletion)
        setOpenDeleteDialog(false)
    }

    //setting default
    const onChangeDefault = (id: string, value: boolean) => {
        // Call API to update rule set default
        const ruleset = fetchedRuleSets?.data?.find(
            (ruleSet) => ruleSet.id === id,
        )
        updateRuleSetName({
            id: ruleset?.id || '',
            name: ruleset?.name || '',
            readOnly: ruleset?.readOnly || true,
            rules: ruleset?.rules ?? [],
            isDefault: value,
        })
    }

    if (isLoadingRuleSets) {
        return <LinearProgress />
    }

    return (
        <Box sx={{ padding: '10px 15px' }}>
            <Box
                display="flex"
                justifyContent="flex-end"
                sx={{
                    marginBottom: '20px',
                }}
            >
                {hasWriteAccess && (
                    <Button
                        id="addNewRuleSet"
                        variant="contained"
                        onClick={() => setNewRuleSet(true)}
                    >
                        {t('addNewbussiness')}
                    </Button>
                )}
            </Box>
            <Box
                sx={{
                    maxHeight: `${maxHeight}`,
                    overflow: 'auto',
                }}
            >
                {newRuleSet && (
                    <Box display="flex" alignItems="center" mb={5}>
                        <Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <TextField
                                    id="newRuleSetName"
                                    placeholder={t('enterNewRuleSet')}
                                    value={newRuleSetName}
                                    onChange={(e) => {
                                        setNewRuleSetName(e.target.value)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleNewRuleSetCreation()
                                        }
                                    }}
                                    autoFocus
                                />
                                <Button
                                    id="saveRuleSet"
                                    onClick={() => handleNewRuleSetCreation()}
                                    sx={{
                                        minWidth: '30px',
                                        marginLeft: '10px',
                                    }}
                                >
                                    <SaveIcon />
                                </Button>
                                <Button
                                    id="cancelRuleSet"
                                    onClick={() => cancelNewRuleSetCreation()}
                                    sx={{
                                        minWidth: '25px',
                                        width: '25px',
                                        marginLeft: '5px',
                                    }}
                                >
                                    <CloseIcon
                                        sx={{
                                            width: '30px',
                                            color: 'red',
                                        }}
                                    />
                                </Button>
                            </Box>
                        </Box>
                        <Box flex="auto">
                            <Divider sx={{ marginLeft: 2 }} />
                        </Box>
                    </Box>
                )}

                {fetchedRuleSets?.data?.map(
                    (ruleSet: IRuleSet, index: number) => (
                        <Box
                            display="flex"
                            flexDirection="column"
                            key={ruleSet.id + ruleSet.name}
                            id={ruleSet.name + '-table'}
                            mt={index === 0 ? 0 : 4}
                        >
                            <Box sx={{ width: '100%' }}>
                                {editingRuleSet?.id === ruleSet.id ? (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <TextField
                                                    id={
                                                        ruleSet.name +
                                                        '-editRuleSetName'
                                                    }
                                                    value={editingRuleSet?.name}
                                                    onChange={(e) =>
                                                        setEditingRuleSet(
                                                            (prev) => {
                                                                return {
                                                                    ...defultEditRuleSetValues,
                                                                    ...prev,
                                                                    name: e
                                                                        .target
                                                                        .value,
                                                                }
                                                            },
                                                        )
                                                    }
                                                />
                                                <Box
                                                    sx={{
                                                        marginLeft: '10px',
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={t(
                                                            'rulesetDefaultInfo',
                                                        )}
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <Switch
                                                            id={
                                                                ruleSet.name +
                                                                '-default-switch-icon'
                                                            }
                                                            checked={
                                                                !!editingRuleSet?.isDefault
                                                            }
                                                            onChange={(e) =>
                                                                setEditingRuleSet(
                                                                    (prev) => {
                                                                        return {
                                                                            ...defultEditRuleSetValues,
                                                                            ...prev,
                                                                            isDefault:
                                                                                e
                                                                                    .target
                                                                                    .checked,
                                                                        }
                                                                    },
                                                                )
                                                            }
                                                            sx={{
                                                                marginRight:
                                                                    '5px',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                                <Tooltip
                                                    title={'Save'}
                                                    placement="top"
                                                    arrow
                                                >
                                                    <Button
                                                        id={
                                                            ruleSet.name +
                                                            '-save-edit-rule-set-name'
                                                        }
                                                        onClick={
                                                            saveEditRuleSetName
                                                        }
                                                        sx={{
                                                            minWidth: '30px',
                                                        }}
                                                    >
                                                        <SaveIcon />
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip
                                                    title={t('cancel')}
                                                    placement="top"
                                                    arrow
                                                >
                                                    <Button
                                                        id={
                                                            ruleSet.name +
                                                            '-cancel-edit-rule-set-name'
                                                        }
                                                        onClick={
                                                            cancelEditRuleSetName
                                                        }
                                                        sx={{
                                                            minWidth: '25px',
                                                            width: '25px',
                                                            marginLeft: '5px',
                                                        }}
                                                    >
                                                        <CloseIcon
                                                            sx={{
                                                                width: '30px',
                                                                color: 'red',
                                                            }}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            </Box>
                                            <Box sx={{ flex: '1' }}>
                                                <Divider
                                                    sx={{ marginLeft: 2 }}
                                                />
                                            </Box>
                                        </Box>
                                        {/* <Box
                                            sx={{
                                                margin: '10px 0px 10px 3px',
                                            }}
                                        >
                                            <CheckboxInput
                                                id={ruleSet.id}
                                                label="Default"
                                                value={
                                                    !!editingRuleSet?.isDefault
                                                }
                                                onChange={(
                                                    id: string,
                                                    value: boolean,
                                                ) =>
                                                    setEditingRuleSet(
                                                        (prev) => {
                                                            return {
                                                                ...defultEditRuleSetValues,
                                                                ...prev,
                                                                isDefault:
                                                                    value,
                                                            }
                                                        },
                                                    )
                                                }
                                            />
                                        </Box> */}
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="h5"
                                                    id={
                                                        ruleSet.name +
                                                        'ruleset-name'
                                                    }
                                                >
                                                    {ruleSet.name}
                                                </Typography>
                                                <Tooltip
                                                    title={t(
                                                        'rulesetDefaultInfo',
                                                    )}
                                                    placement="top"
                                                    arrow
                                                >
                                                    <Box
                                                        sx={{
                                                            marginLeft: '10px',
                                                        }}
                                                    >
                                                        <Switch
                                                            id={
                                                                ruleSet.name +
                                                                '-default-rule-set'
                                                            }
                                                            checked={
                                                                ruleSet.isDefault
                                                            }
                                                            disabled={
                                                                hasWriteAccess
                                                                    ? ruleSet.readOnly
                                                                    : true
                                                            }
                                                            onChange={(e) =>
                                                                onChangeDefault(
                                                                    ruleSet.id,
                                                                    e.target
                                                                        .checked,
                                                                )
                                                            }
                                                            sx={{
                                                                marginRight:
                                                                    '5px',
                                                            }}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                                {hasWriteAccess &&
                                                !ruleSet.readOnly ? (
                                                    <Box
                                                        sx={{ display: 'flex' }}
                                                    >
                                                        <Tooltip
                                                            title={t('edit')}
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <Button
                                                                id={
                                                                    ruleSet.name +
                                                                    '-edit-business-rule-button'
                                                                }
                                                                onClick={() =>
                                                                    handleEditClick(
                                                                        ruleSet,
                                                                    )
                                                                }
                                                                sx={{
                                                                    minWidth:
                                                                        '30px',
                                                                }}
                                                            >
                                                                <img
                                                                    src="/assets/images/icons/edit.svg"
                                                                    alt="Edit Rule Set"
                                                                />
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={t('delete')}
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <IconButton
                                                                id={
                                                                    ruleSet.name +
                                                                    '-delete-business-rule-button'
                                                                }
                                                                color="error"
                                                                onClick={() =>
                                                                    openDeleteConfirmModal(
                                                                        ruleSet.id,
                                                                    )
                                                                }
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <> </>
                                                )}
                                            </Box>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    flex: '1',
                                                }}
                                            >
                                                <Divider
                                                    sx={{ marginLeft: 2 }}
                                                />
                                            </Box>
                                        </Box>
                                        {/* <Box
                                            sx={{
                                                marginBottom: '10px',
                                                marginTop: '5px',
                                            }}
                                        >
                                            <CheckboxInput
                                                id={ruleSet.id}
                                                label="Default"
                                                value={ruleSet.isDefault}
                                                disabled={ruleSet.readOnly}
                                                onChange={onChangeDefault}
                                            />
                                        </Box> */}
                                    </Box>
                                )}
                            </Box>
                            <Box>
                                <RuleSet
                                    {...ruleSet}
                                    readOnly={
                                        hasWriteAccess ? ruleSet.readOnly : true
                                    }
                                    columns={fetchedRuleSets.columns}
                                    setReload={setReload}
                                />
                            </Box>
                        </Box>
                    ),
                )}
            </Box>
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={() => handleDeleteDialogOperation()}
                handleClose={() => setOpenDeleteDialog(false)}
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('confirmDeleteRuleSet')}
            />
        </Box>
    )
}

export default BusinessRules
